import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Building2, MapPin, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useSEO } from "@/lib/useSEO";

const PAGE_SIZE = 10;

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  useSEO({
    title: "1,100+ Global GCC Companies — Master Directory | KARM",
    description: "Explore Global Capability Centres worldwide — Goldman Sachs, Citi, HSBC, JP Morgan, Amazon, Google, Deutsche Bank, Wells Fargo and 1,100+ more. Direct links to every employer's careers page.",
    path: "/companies",
  });

  useEffect(() => {
    api.get("/companies", { params: { limit: 2000 } }).then((r) => setCompanies(r.data || []));
  }, []);

  const filtered = companies.filter((c) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    const cityText = Array.isArray(c.cities) ? c.cities.join(", ") : (c.cities || "");
    return (
      c.name.toLowerCase().includes(needle) ||
      (c.sector || "").toLowerCase().includes(needle) ||
      cityText.toLowerCase().includes(needle)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="container py-10">
      <p className="eyebrow mb-1">GCC Master Directory</p>
      <h1 className="display-2 mb-6">{companies.length.toLocaleString()}+ Global Capability Centers worldwide.</h1>

      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setPage(1); }}
        placeholder="Search company, sector, city…"
        className="max-w-xl mb-10 h-12"
        data-testid="companies-search"
      />

      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-muted-foreground" data-testid="companies-count">
          {filtered.length.toLocaleString()} companies · page {page} of {totalPages}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="companies-grid">
        {pageItems.map((c) => (
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/jobs?q=${encodeURIComponent(c.name)}`)}
            onKeyDown={(e) => { if (e.key === "Enter") navigate(`/jobs?q=${encodeURIComponent(c.name)}`); }}
            key={c.company_id}
            className="vital-card p-5 flex items-start gap-3 cursor-pointer"
            data-testid={`company-card-${c.company_id}`}
          >
            <div className="h-10 w-10 rounded-md bg-muted border border-border flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold truncate">{c.name}</div>
              <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                <MapPin className="h-3 w-3" />{Array.isArray(c.cities) ? c.cities.join(", ") : (c.cities || "—")}
                {c.india_headcount && (
                  <span
                    className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-muted"
                    data-testid={`headcount-pill-${c.company_id}`}
                  >
                    {c.india_headcount}
                  </span>
                )}
              </div>
              {(c.gcc_notes || c.notes) && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.gcc_notes || c.notes}</p>}
              {c.external_apply_only && (
                <span
                  className="inline-flex items-center gap-1 mt-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                  data-testid={`external-pill-${c.company_id}`}
                >
                  Apply on company site
                </span>
              )}
            </div>
            <a
              href={c.careers_url || "/auth"}
              target={c.careers_url ? "_blank" : undefined}
              rel="noreferrer"
              onClick={(e) => { e.stopPropagation(); if (!c.careers_url) { e.preventDefault(); navigate("/auth"); } }}
              className="text-muted-foreground hover:text-primary p-1"
              data-testid={`company-careers-${c.company_id}`}
              aria-label={c.careers_url ? "Open careers site" : "Sign up to see careers URL"}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4" data-testid="companies-pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="companies-prev-page"
            className="inline-flex items-center gap-1 text-sm font-semibold disabled:opacity-30 hover:text-primary"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            data-testid="companies-next-page"
            className="inline-flex items-center gap-1 text-sm font-semibold disabled:opacity-30 hover:text-primary"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

